// Supplier performance dashboard API — monthly delivery and quality uploads
import api, { type AxiosResponse } from './client';

/** Percentages are 0–100 averaged over lines that could be judged; null when none could. */
export interface PerformanceMeasures {
  on_time: number | null;
  otif: number | null;
  required: number | null;
  sla: number | null;
  call_off: number | null;
}

export interface PerformanceTotals extends PerformanceMeasures {
  lines: number;
  suppliers: number;
  care_cases: number;
  quantity: number;
  ncrs: number;
}

export interface PerformanceMonth extends PerformanceTotals {
  /** YYYY-MM */
  period: string;
  /** e.g. "Sep 25" */
  label: string;
}

export interface SupplierPerformanceRow extends PerformanceMeasures {
  id: number;
  name: string;
  lines: number;
  months_reported: number;
  /** Months in the range whose on-time % was under the target. */
  months_below_target: number;
  /** The latest unbroken run of months under target. */
  consecutive_below_target: number;
}

export interface DivisionPerformanceRow extends PerformanceMeasures {
  /** Company id, or "unassigned" for lines whose PO site has no division. */
  id: number | 'unassigned';
  name: string;
  lines: number;
  suppliers: number;
}

export interface PerformanceFilterOption {
  id: number;
  name: string;
}

export interface SupplierPerformanceDashboard {
  empty: boolean;
  range?: {
    from: string;
    to: string;
    months: number;
    prior_from: string;
    first_data: string;
    latest_data: string;
    /** Newest month with a normal volume; the default window ends here. */
    latest_complete: string;
  };
  target: { on_time: number; otif: number };
  kpis?: { current: PerformanceTotals; prior: PerformanceTotals };
  monthly?: PerformanceMonth[];
  suppliers?: SupplierPerformanceRow[];
  divisions?: DivisionPerformanceRow[];
  filters?: { divisions: PerformanceFilterOption[]; suppliers: PerformanceFilterOption[] };
  coverage?: { lines: number; with_division: number; quality_ignores_division: boolean };
}

/** The top view switcher's selection (one of these, or none for everything the user can see). */
export interface PerformanceViewParams {
  group?: number;
  company?: number;
  site?: number;
}

export interface SupplierPerformanceParams extends PerformanceViewParams {
  /** YYYY-MM */
  from?: string;
  /** YYYY-MM */
  to?: string;
  /** Division company id or "unassigned". */
  division?: string;
  supplier?: string;
}

/** One delivery line behind a chart month. Flags are null when the line could not be judged. */
export interface DrilldownDeliveryLine {
  id: number;
  supplier_id: number;
  supplier: string;
  division: string;
  po_number: string;
  site_plot: string;
  call_off_date: string | null;
  required_date: string | null;
  agreed_date: string | null;
  sla_date: string | null;
  delivered_date: string;
  /** Actual minus agreed; negative when early. */
  days_late: number | null;
  order_value: number;
  on_time: boolean | null;
  otif: boolean | null;
  required: boolean | null;
  call_off: boolean | null;
}

export interface DrilldownQualityLine {
  id: number;
  supplier_id: number;
  supplier: string;
  ncr_number: string;
  issue_date: string;
  category: string;
  severity: string;
  status: string;
  description: string;
  care_cases: number;
  quantity: number;
  is_ncr: boolean;
}

export interface DrilldownDeliverySupplier extends PerformanceMeasures {
  id: number;
  name: string;
  lines: number;
  late_lines: number;
}

export interface DrilldownQualitySupplier {
  id: number;
  name: string;
  lines: number;
  care_cases: number;
  quantity: number;
  ncrs: number;
}

interface DrilldownBase {
  /** YYYY-MM */
  month: string;
  /** e.g. "March 2026" */
  label: string;
  lines_total: number;
  /** True when more lines exist than were returned. */
  lines_truncated: boolean;
}

export interface DeliveryDrilldown extends DrilldownBase {
  kind: 'delivery';
  summary: PerformanceMeasures & { lines: number; suppliers: number };
  suppliers: DrilldownDeliverySupplier[];
  lines: DrilldownDeliveryLine[];
}

export interface QualityDrilldown extends DrilldownBase {
  kind: 'quality';
  summary: { lines: number; care_cases: number; quantity: number; ncrs: number };
  suppliers: DrilldownQualitySupplier[];
  lines: DrilldownQualityLine[];
  /** Quality files carry no division, so a division filter was not applied. */
  ignores_division: boolean;
}

export type SupplierPerformanceDrilldown = DeliveryDrilldown | QualityDrilldown;

export interface SupplierPerformanceDrilldownParams extends PerformanceViewParams {
  /** YYYY-MM */
  month: string;
  kind: 'delivery' | 'quality';
  division?: string;
  supplier?: string;
}

export const supplierPerformanceAPI = {
  getDashboard: (params?: SupplierPerformanceParams): Promise<AxiosResponse<SupplierPerformanceDashboard>> =>
    api.get('/barratt/supplier-performance/dashboard/', { params }),

  getDrilldown: (params: SupplierPerformanceDrilldownParams): Promise<AxiosResponse<SupplierPerformanceDrilldown>> =>
    api.get('/barratt/supplier-performance/drilldown/', { params }),
};
